// Step 4: Ruleset Templates

import { useState, useEffect } from 'react';
import { Check, Copy, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { WizardStepProps, SelectedPayer } from '@/lib/onboarding/types';
import { RULESET_TEMPLATES } from '@/lib/onboarding/types';
import { useClientOnboarding } from '@/hooks/useClientOnboarding';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PlanRulesetConfig {
  payer_id: string;
  payer_name: string;
  plan_id: string;
  plan_name: string;
  plan_type: string;
  selected_template: string | null;
  cloned: boolean;
}

export function Step4RulesetTemplates({ clientId, progress, onComplete, onBack }: WizardStepProps) {
  const { completeStep, updateProgress, isCompleting } = useClientOnboarding(clientId);
  
  const [planConfigs, setPlanConfigs] = useState<PlanRulesetConfig[]>([]);
  const [cloningPlan, setCloningPlan] = useState<string | null>(null);
  const [cloneProgress, setCloneProgress] = useState(0);

  // Initialize plan configs from selected payers
  useEffect(() => {
    const configs: PlanRulesetConfig[] = [];
    progress.selected_payers.forEach(payer => {
      payer.plans.forEach(plan => {
        configs.push({
          payer_id: payer.payer_id,
          payer_name: payer.payer_name,
          plan_id: plan.plan_id,
          plan_name: plan.plan_name,
          plan_type: plan.plan_type,
          selected_template: plan.selected_ruleset_template,
          cloned: false,
        });
      });
    });
    setPlanConfigs(configs);
  }, [progress.selected_payers]);

  const updatePlanTemplate = (planId: string, templateId: string) => {
    setPlanConfigs(prev => prev.map(config => 
      config.plan_id === planId 
        ? { ...config, selected_template: templateId, cloned: false }
        : config
    ));
  };

  const cloneRuleset = async (config: PlanRulesetConfig) => {
    if (!config.selected_template) return;
    
    setCloningPlan(config.plan_id);
    setCloneProgress(0);

    try {
      // Simulate cloning process with progress
      for (let i = 0; i <= 100; i += 20) {
        setCloneProgress(i);
        await new Promise(r => setTimeout(r, 200));
      }

      // Get template data (in production, fetch from rulesets table)
      const template = RULESET_TEMPLATES.find(t => t.id === config.selected_template);
      
      // Create client-specific ruleset
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('client_rulesets')
        .insert({
          client_id: clientId,
          source_ruleset_id: null, // Would be actual template ID in production
          payer_id: null, // Would need actual payer UUID
          plan_id: null, // Would need actual plan UUID
          ruleset_name: `${config.payer_name} ${config.plan_type} - ${template?.name}`,
          ruleset_version: 1,
          status: 'active',
          rules: [], // Would clone actual rules in production
          created_by: user?.id,
        });

      if (error) throw error;

      setPlanConfigs(prev => prev.map(c => 
        c.plan_id === config.plan_id ? { ...c, cloned: true } : c
      ));
      
      toast.success(`Ruleset cloned for ${config.payer_name} ${config.plan_type}`);
    } catch (error) {
      toast.error('Failed to clone ruleset');
    } finally {
      setCloningPlan(null);
      setCloneProgress(0);
    }
  };

  const cloneAllRulesets = async () => {
    const toClone = planConfigs.filter(c => c.selected_template && !c.cloned);
    
    for (const config of toClone) {
      await cloneRuleset(config);
    }
  };

  const allConfigured = planConfigs.every(c => c.selected_template);
  const allCloned = planConfigs.every(c => c.cloned);

  const handleSubmit = () => {
    // Update progress with selected templates
    const updatedPayers: SelectedPayer[] = progress.selected_payers.map(payer => ({
      ...payer,
      plans: payer.plans.map(plan => {
        const config = planConfigs.find(c => c.plan_id === plan.plan_id);
        return {
          ...plan,
          selected_ruleset_template: config?.selected_template || null,
        };
      }),
    }));

    completeStep({
      step: 4,
      stepData: {
        selected_payers: updatedPayers,
      },
    }, {
      onSuccess: onComplete,
    });
  };

  const getRecommendedTemplate = (payerId: string, planType: string) => {
    if (payerId === 'ohio_bwc') return 'BWC_OH_BASE_2026';
    return 'COMMERCIAL_BASE_TEMPLATE_V1';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ruleset Templates</CardTitle>
        <CardDescription>
          Select and clone ruleset templates for each payer/plan combination.
          These will be client-specific and can be customized after go-live.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Plan configurations */}
        <div className="space-y-4">
          {planConfigs.map(config => {
            const recommended = getRecommendedTemplate(config.payer_id, config.plan_type);
            const isCloning = cloningPlan === config.plan_id;

            return (
              <div
                key={config.plan_id}
                className={cn(
                  'p-4 border rounded-lg space-y-3',
                  config.cloned && 'border-green-500 bg-green-50/50'
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">{config.payer_name}</span>
                    <Badge variant="outline" className="ml-2">{config.plan_type}</Badge>
                  </div>
                  {config.cloned && (
                    <Badge variant="default" className="bg-green-500">
                      <Check className="mr-1 h-3 w-3" />
                      Cloned
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <Select
                    value={config.selected_template || ''}
                    onValueChange={(v) => updatePlanTemplate(config.plan_id, v)}
                    disabled={config.cloned}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {RULESET_TEMPLATES.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            {template.name}
                            {template.id === recommended && (
                              <Badge variant="secondary" className="text-xs">Recommended</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => cloneRuleset(config)}
                    disabled={!config.selected_template || config.cloned || isCloning}
                  >
                    {isCloning ? (
                      'Cloning...'
                    ) : config.cloned ? (
                      <>
                        <Check className="mr-1 h-4 w-4" />
                        Done
                      </>
                    ) : (
                      <>
                        <Copy className="mr-1 h-4 w-4" />
                        Clone
                      </>
                    )}
                  </Button>
                </div>

                {isCloning && (
                  <Progress value={cloneProgress} className="h-2" />
                )}
              </div>
            );
          })}
        </div>

        {/* Bulk clone button */}
        {allConfigured && !allCloned && (
          <Button
            variant="secondary"
            onClick={cloneAllRulesets}
            disabled={!!cloningPlan}
            className="w-full"
          >
            <Copy className="mr-2 h-4 w-4" />
            Clone All Rulesets
          </Button>
        )}

        {/* Warning if not all cloned */}
        {allConfigured && !allCloned && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please clone all rulesets before continuing. Each client needs their own 
              copy of the ruleset templates to allow customization.
            </AlertDescription>
          </Alert>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!allConfigured || !allCloned || isCompleting}
          >
            {isCompleting ? 'Saving...' : 'Continue to SLA Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
